import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Building2, GitBranch } from "lucide-react";
import { api } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";

export function GlobalSearch({ open, setOpen }) {
  const navigate = useNavigate();
  const { tenantId, setTenantId } = useTenant();
  const [q, setQ] = useState("");
  const [res, setRes] = useState({ documents: [], organizations: [], processes: [] });
  const timer = useRef();

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setRes({ documents: [], organizations: [], processes: [] }); return; }
    timer.current = setTimeout(() => {
      const p = tenantId ? `&tenant_id=${tenantId}` : "";
      api.get(`/search?q=${encodeURIComponent(q.trim())}${p}`).then((r) => setRes(r.data)).catch(() => {});
    }, 220);
    return () => clearTimeout(timer.current);
  }, [q, tenantId]);

  const go = (path) => { setOpen(false); setQ(""); navigate(path); };
  const total = res.documents.length + res.organizations.length + res.processes.length;

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(""); }}>
      <DialogContent className="overflow-hidden p-0 gap-0 max-w-xl" data-testid="global-search-dialog">
        <DialogTitle className="sr-only">Ricerca globale</DialogTitle>
        <DialogDescription className="sr-only">Cerca documenti, clienti e processi</DialogDescription>
        <Command shouldFilter={false}
          className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-slate-400 [&_[cmdk-group-heading]]:font-semibold">
          <CommandInput value={q} onValueChange={setQ} placeholder="Cerca documenti, clienti, processi..." data-testid="global-search-input" />
          <CommandList className="max-h-[360px]">
            {q.trim().length >= 2 && total === 0 && <CommandEmpty>Nessun risultato.</CommandEmpty>}
            {res.documents.length > 0 && (
              <CommandGroup heading="Documenti">
                {res.documents.map((d) => (
                  <CommandItem key={d.id} value={"doc-" + d.id} onSelect={() => go(`/documenti/${d.id}`)}
                    className="gap-2 cursor-pointer" data-testid={`search-doc-${d.id}`}>
                    <FileText className="text-blue-700" />
                    {d.codice ? <span className="font-mono text-xs text-slate-500">{d.codice}</span> : null}
                    <span className="truncate">{d.titolo}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {res.organizations.length > 0 && (
              <CommandGroup heading="Clienti">
                {res.organizations.map((o) => (
                  <CommandItem key={o.id} value={"org-" + o.id} onSelect={() => { setTenantId(o.id); go("/documenti"); }}
                    className="gap-2 cursor-pointer" data-testid={`search-org-${o.id}`}>
                    <Building2 className="text-indigo-600" />
                    <span className="truncate">{o.nome}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {res.processes.length > 0 && (
              <CommandGroup heading="Processi">
                {res.processes.map((p) => (
                  <CommandItem key={p.id} value={"proc-" + p.id} onSelect={() => { if (p.tenant_id) setTenantId(p.tenant_id); go("/documenti"); }}
                    className="gap-2 cursor-pointer" data-testid={`search-proc-${p.id}`}>
                    <GitBranch className="text-slate-500" />
                    <span className="truncate">{p.nome}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
